import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnDestroy,
    type OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';

import { getGridVariants } from '@web/store/page-structure/grids/grids.models';
import { tariffsCssClasses } from '@web/core/constants/tariff.const';
import { UserFacade } from '@web/store/user/user.facade';
import { PaidFeaturesModalComponent } from '@web/shared/modals/paid-features-modal/paid-features-modal.component';

@Component({
    selector: 'web-toolbar-tab',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    templateUrl: './web-toolbar-tab.component.html',
    styleUrls: ['./web-toolbar-tab.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarTabComponent implements OnInit, OnDestroy {
    @Input() public isAdmin: boolean;
    @Input() public isEduSite: boolean;

    @Output() public onClick: EventEmitter<number> = new EventEmitter<number>();

    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly modalService: BsModalService = inject(BsModalService);

    private readonly unsubscribe$ = new Subject<void>();

    public hasAccess: boolean;
    public badgeTariffClass: string;

    private _variants: number[] = null;
    public get variantOptions(): number[] {
        if (!this._variants) {
            return (this._variants = Object.entries(getGridVariants())
                .filter(([_, i]) => !i.hidden)
                .map(([k]) => Number(k)));
        }

        return this._variants;
    }

    public ngOnInit(): void {
        if (this.isAdmin || this.isEduSite) {
            this.hasAccess = true;
            return;
        }

        this.userFacade.hasAccessToTab$.pipe(takeUntil(this.unsubscribe$)).subscribe(({ hasAccess, nextTariff }) => {
            if (nextTariff) {
                this.badgeTariffClass = tariffsCssClasses[nextTariff];
            }

            this.hasAccess = hasAccess;
        });
    }

    public onSelect(variant: number): void {
        if (!this.hasAccess && !this.isAdmin) {
            this.showPaidFeaturesModal();
            return;
        }

        this.onClick.emit(variant);
    }

    public showPaidFeaturesModal(): void {
        if (!this.hasAccess) {
            setTimeout(() => {
                this.modalService.show(PaidFeaturesModalComponent, { class: 'modal-lg', backdrop: true });
            }, 300);
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
